
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function HowItWorks() {
  const [isVisible, setIsVisible] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'How It Works - Negotrix | Commercial Intelligence & Negotiation Process';
    setTimeout(() => setIsVisible(true), 100);
  }, []);

  const steps = [
    {
      num: '01',
      title: 'Free Deep-Dive Analysis',
      timeline: 'Week 1–2',
      desc: 'We conduct a comprehensive analysis of your entire spend portfolio with zero upfront cost.',
      points: ['Data collection & spend mapping', 'Technology stack assessment', 'Contract & vendor review', 'Initial opportunity identification'],
    },
    {
      num: '02',
      title: 'Commercial Roadmap',
      timeline: 'Week 2–3',
      desc: 'You receive a detailed commercial roadmap with prioritized opportunities and clear ROI projections.',
      points: ['Prioritized commercial opportunities', 'ROI projections', 'Implementation timeline', 'Risk assessment'],
    },
    {
      num: '03',
      title: 'Implementation',
      timeline: 'Week 3 onward',
      desc: 'We work with you to implement recommendations, negotiating directly with vendors on your behalf.',
      points: ['Direct vendor negotiations', 'System improvements & configurations', 'Verified outcome tracking & reporting'],
    },
    {
      num: '04',
      title: 'Continuous Commercial Intelligence',
      timeline: 'Ongoing',
      desc: 'We monitor your spend continuously, track all outcomes, and identify new opportunities as your business evolves.',
      points: ['Ongoing spend monitoring', 'Outcome verification', 'New opportunity identification'],
    },
  ];

  return (
    <div className={`min-h-screen bg-cipher-black text-white transition-opacity duration-700 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
      <Header />

      {/* Hero */}
      <section className="pt-40 pb-24 px-6 lg:px-12">
        <div className="max-w-[1400px] mx-auto">
          <h2 className="text-sm tracking-widest uppercase text-cipher-hypergreen font-bold mb-6">The Process</h2>
          <h1 className="text-[9vw] leading-[0.9] font-black tracking-tighter mb-12">
            <span className="text-white">Four Steps.</span><br />
            <span className="text-cipher-hypergreen">Zero Risk.</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl">
            From initial analysis to ongoing commercial intelligence, our process is designed to deliver better commercial outcomes with
            <span className="text-white font-medium ml-1">no upfront investment required.</span>
          </p>
        </div>
      </section>

      {/* Steps */}
      <section className="pb-32 bg-cipher-black">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="border-t border-gray-800">
            {steps.map((step) => (
              <div key={step.num} className="group border-b border-gray-800 py-16 hover:bg-cipher-dark transition-colors px-6 -mx-6">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                  <div className="lg:col-span-1 text-gray-600 font-black text-2xl">{step.num}</div>
                  <div className="lg:col-span-4">
                    <div className="text-3xl lg:text-5xl font-black group-hover:text-cipher-hypergreen transition-colors mb-3">
                      {step.title}
                    </div>
                    <div className="text-cipher-hypergreen text-sm font-bold tracking-widest uppercase">{step.timeline}</div>
                  </div>
                  <div className="lg:col-span-7">
                    <p className="text-gray-400 text-lg leading-relaxed mb-6">{step.desc}</p>
                    <ul className="space-y-2">
                      {step.points.map((p, i) => (
                        <li key={i} className="flex items-center space-x-3 text-gray-500">
                          <span className="w-1.5 h-1.5 bg-cipher-hypergreen rounded-full flex-shrink-0"></span>
                          <span>{p}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Model */}
      <section className="py-24 bg-cipher-dark border-t border-gray-900">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <h2 className="text-sm tracking-widest uppercase text-cipher-hypergreen font-bold mb-6">Pricing</h2>
          <h3 className="text-5xl lg:text-7xl font-black tracking-tight mb-20">Success-Based<br />Pricing Model.</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 border-t border-gray-800 pt-16">
            {[
              { val: 'Zero', label: 'Upfront Fees', desc: 'We invest our time and expertise upfront with no payment required.' },
              { val: '%', label: 'Pay on Results', desc: 'We earn a percentage only of the verified outcomes we deliver.' },
              { val: '✓', label: 'Verified Outcomes', desc: 'All outcomes are tracked and verified through your financial systems.' },
            ].map((item, i) => (
              <div key={i} className="p-8 bg-cipher-black rounded-[2rem] border border-gray-800 hover:border-cipher-hypergreen transition-colors">
                <div className="text-6xl font-black text-cipher-hypergreen mb-4">{item.val}</div>
                <div className="text-2xl font-bold text-white mb-3">{item.label}</div>
                <p className="text-gray-400">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24 bg-cipher-black border-t border-gray-900">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <h2 className="text-sm tracking-widest uppercase text-cipher-hypergreen font-bold mb-16">Typical Timeline</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { days: '14', label: 'Days for Complete Analysis', desc: 'From data collection to commercial report delivery' },
              { days: '30', label: 'Days for Quick Wins', desc: 'First commercial outcomes realized through immediate improvements' },
              { days: '90', label: 'Days for Full Implementation', desc: 'All recommendations implemented and outcomes verified' },
            ].map((t, i) => (
              <div key={i}>
                <div className="text-7xl lg:text-9xl font-black text-white mb-4">{t.days}</div>
                <div className="text-xl font-bold text-cipher-hypergreen mb-2">{t.label}</div>
                <p className="text-gray-400">{t.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-32 lg:py-48 bg-cipher-dark border-t border-gray-800">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-[8vw] leading-[0.9] font-black tracking-tighter text-white mb-12">
             Build commercial<br /><span className="text-cipher-hypergreen">confidence in 14 days.</span>
          </h2>
          <button
            onClick={() => navigate('/contact')}
            className="inline-flex items-center justify-center px-10 py-5 text-lg tracking-wide font-bold uppercase rounded-full text-cipher-black bg-cipher-hypergreen hover:bg-white transition-all duration-300"
          >
            Start Free Analysis
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
